import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'pushPromptDismissedAt';
const DISMISS_HOURS = 72; // re-show prompt after 72h if dismissed

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushPermissionPrompt() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

  useEffect(() => {
    if (!supported) return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
    try {
      const last = localStorage.getItem(DISMISS_KEY);
      if (last) {
        const diffMs = Date.now() - Number(last);
        if (diffMs < DISMISS_HOURS * 3600 * 1000) return;
      }
    } catch {}
    // Small delay to avoid spamming on first paint
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        setShow(false);
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: keyRes } = await axios.get(`${API}/api/push/vapid-key`);
      const applicationServerKey = urlBase64ToUint8Array(keyRes.public_key);
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      }
      await axios.post(`${API}/api/push/subscribe`, {
        subscription: sub.toJSON(),
        userAgent: navigator.userAgent,
      });
      toast.success('Notificações ativadas');
      setShow(false);
    } catch (e) {
      console.error('Push subscribe failed', e);
      toast.error('Não foi possível ativar notificações');
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 flex items-start gap-3" data-testid="push-prompt-banner">
      <div className="w-9 h-9 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
        <Bell size={18} className="text-brand-green" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-brand-text">Recebe lembretes importantes</p>
        <p className="text-xs text-brand-muted mt-0.5">Ativa as notificações para receberes avisos de consultas, lembretes de água, lanches e do diário alimentar.</p>
        <div className="flex gap-2 mt-3">
          <button onClick={enable} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-50" data-testid="push-enable-btn">{busy ? 'A ativar…' : 'Ativar notificações'}</button>
          <button onClick={dismiss} className="text-brand-muted text-xs px-2" data-testid="push-dismiss-btn">Agora não</button>
        </div>
      </div>
      <button onClick={dismiss} className="text-brand-placeholder hover:text-brand-text flex-shrink-0" aria-label="Fechar">
        <X size={16} />
      </button>
    </div>
  );
}
